import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { BlockInstanceType } from 'application-types';

import { useSignedUrlRefresh } from 'react-hooks';

type ProcessedBlockInstanceType = BlockInstanceType & {
  _urlRefreshKey?: number;
};

interface BlockContentWithSignedUrlRefreshProps {
  block: BlockInstanceType | null | undefined;
  children: (
    processedBlock: ProcessedBlockInstanceType | null | undefined
  ) => React.ReactNode;
  onUrlRefreshed?: (mediaId: string, newUrl: string) => void;
}

/**
 * Component that wraps block content and automatically refreshes signed URLs
 * for PDF attachments in linked rows when they expire.
 */
export function BlockContentWithSignedUrlRefresh({
  block,
  children,
  onUrlRefreshed,
}: BlockContentWithSignedUrlRefreshProps) {
  const [processedBlock, setProcessedBlock] = useState<
    ProcessedBlockInstanceType | null | undefined
  >(block);
  const isMountedRef = useRef(true);
  const processBlockContentRef = useRef<
    | ((
        block: BlockInstanceType | null | undefined
      ) => Promise<BlockInstanceType | null | undefined>)
    | null
  >(null);

  const { processBlockContent, cleanup } = useSignedUrlRefresh(
    useMemo(
      () => ({
        onUrlRefreshed: (mediaId: string, newUrl: string) => {
          if (onUrlRefreshed) {
            onUrlRefreshed(mediaId, newUrl);
          }

          // Re-process the block to get the updated URLs from the hook's internal storage
          // The hook will use the refreshed URLs it has stored when processing
          if (isMountedRef.current && block && processBlockContentRef.current) {
            processBlockContentRef
              .current(block)
              .then(processed => {
                if (isMountedRef.current && processed) {
                  // Add a refresh timestamp to ensure React sees this as a new object
                  const processedWithKey = {
                    ...processed,
                    _urlRefreshKey: Date.now(),
                  };

                  setProcessedBlock(processedWithKey);
                }
              })
              .catch(error => {
                console.error(
                  'Failed to re-process block after URL refresh:',
                  error
                );
              });
          }
        },
      }),
      [onUrlRefreshed, block]
    )
  );

  processBlockContentRef.current = processBlockContent;

  // Immediately sync processedBlock when block prop changes
  // This prevents showing stale content from previous block while async processing runs
  useEffect(() => {
    setProcessedBlock(block);
  }, [block]);

  const processBlock = useCallback(async () => {
    if (!block) return;

    let processed: BlockInstanceType = block;

    try {
      const result = await processBlockContent(block);

      if (result) {
        processed = result;
      }
    } catch (error) {
      console.error('Failed to process block content:', error);
    }

    // Only update state if component is still mounted
    if (isMountedRef.current) {
      setProcessedBlock(processed);
    }
  }, [block, processBlockContent]);

  useEffect(() => {
    isMountedRef.current = true;
    processBlock();

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [processBlock, cleanup]);

  return <>{children(processedBlock)}</>;
}
